import React from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import renderSelectField from '../../../../shared/components/form/Select';
import Tooltip from '../../../../shared/components/widgets/Tooltip';
import { renderFieldForm } from '../../shared/formComponents';
import { META_GENDER_OPTIONS, LANGUAGE_OPTIONS } from '../../shared/constants';
import GeoTargetingFields from './shared/GeoTargetingFields';
import AudienceFields from './shared/AudienceFields';


/**
 * Meta Budget Fields Component
 * Handles all 3 Meta ad types: Facebook Feed, Instagram Feed, Audience Network
 */
const MetaBudgetFields = ({
  budgetTypeOption,
  setupFlow,
  agencyInternal,
  formValues,
}) => {
  if (!budgetTypeOption) return null;

  const { value: adType } = budgetTypeOption;

  // URL validation helper
  const isValidUrl = (urlString) => {
    if (!urlString) return null; // null = no validation shown
    try {
      const url = new URL(urlString.trim());
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
      return false;
    }
  };

  // Age validation helper
  const validateAge = (age, min = 13, max = 65) => {
    if (!age) return null;
    const ageNum = parseInt(age, 10);
    return !Number.isNaN(ageNum) && ageNum >= min && ageNum <= max;
  };

  // Get validation results
  const urlValidation = isValidUrl(formValues?.masterItemOnList);
  const ageMinValidation = validateAge(formValues?.metaAgeMin, 13, 65);
  const ageMaxValidation = validateAge(formValues?.metaAgeMax, 13, 65);

  // Get display name for the ad type
  let adTypeDisplay = 'Audience Network';
  if (adType === 'meta_facebook') {
    adTypeDisplay = 'Facebook Feed';
  } else if (adType === 'meta_instagram') {
    adTypeDisplay = 'Instagram Feed';
  }

  return (
    <>
      {/* URL Field - Required for all Meta ad types */}
      <div className="form__form-group">
        <span className="form__form-group-label">
          Landing Page URL{' '}
          <Tooltip
            id="metaUrl"
            msg={`Enter the landing page URL for your ${adTypeDisplay} ads`}
            placement="top"
          />
        </span>
        <div className="form__form-group-field">
          <Field
            name="masterItemOnList"
            component={renderFieldForm}
            placeholder="https://..."
          />
        </div>
        {formValues?.masterItemOnList && urlValidation !== null && (
          <div
            style={{
              marginTop: '10px',
              padding: '10px',
              borderRadius: '4px',
              border: `1px solid ${urlValidation ? 'rgba(145, 190, 35, 0.4)' : 'rgba(255, 0, 0, 0.4)'}`,
              backgroundColor: `${urlValidation ? 'rgba(145, 190, 35, 0.1)' : 'rgba(255, 0, 0, 0.1)'}`,
            }}
          >
            <div style={{ fontWeight: '500', color: urlValidation ? '#91BE23' : '#ff4861' }}>
              {urlValidation ? '✓ Valid URL' : '⚠ Invalid URL'}
            </div>
            {!urlValidation && (
              <div style={{ fontSize: '0.9em', color: '#721c24', marginTop: '4px' }}>
                URL must start with http:// or https://
              </div>
            )}
          </div>
        )}
      </div>

      {/* Brand Name - Optional field for all Meta ad types */}
      <div className="form__form-group">
        <span className="form__form-group-label">
          Brand Name (Optional){' '}
          <Tooltip
            id="metaBrandName"
            msg="Enter the brand name associated with this budget. This will be used to identify the brand in budget reports."
            placement="top"
          />
        </span>
        <div className="form__form-group-field">
          <Field
            name="metaBrandName"
            component={renderFieldForm}
            placeholder="Enter brand name"
          />
        </div>
      </div>

      {/* Gender Targeting */}
      <div className="form__form-group">
        <span className="form__form-group-label">
          Gender Targeting{' '}
          <Tooltip
            id="metaGender"
            msg="Select the gender targeting for your Meta ads"
            placement="top"
          />
        </span>
        <div className="form__form-group-field">
          <Field
            name="metaGender"
            component={renderSelectField}
            options={META_GENDER_OPTIONS}
          />
        </div>
      </div>

      {/* Age Targeting */}
      <div className="form__form-group">
        <span className="form__form-group-label">
          Age Range Targeting{' '}
          <Tooltip
            id="metaAge"
            msg="Set the minimum and maximum age for your audience (13-65+)"
            placement="top"
          />
        </span>
        <div className="form__form-group-field" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <div style={{ flex: 1 }}>
            <Field
              name="metaAgeMin"
              component={renderFieldForm}
              type="number"
              placeholder="Min (e.g., 25)"
            />
          </div>
          <span style={{ padding: '0 10px' }}>to</span>
          <div style={{ flex: 1 }}>
            <Field
              name="metaAgeMax"
              component={renderFieldForm}
              type="number"
              placeholder="Max (e.g., 55)"
            />
          </div>
        </div>
        {(formValues?.metaAgeMin || formValues?.metaAgeMax) && (
          <div
            style={{
              marginTop: '10px',
              padding: '10px',
              borderRadius: '4px',
              border: `1px solid ${(ageMinValidation && ageMaxValidation) ? 'rgba(145, 190, 35, 0.4)' : 'rgba(255, 165, 0, 0.4)'}`,
              backgroundColor: `${(ageMinValidation && ageMaxValidation) ? 'rgba(145, 190, 35, 0.1)' : 'rgba(255, 165, 0, 0.1)'}`,
            }}
          >
            <div style={{ fontWeight: '500', color: (ageMinValidation && ageMaxValidation) ? '#91BE23' : '#856404' }}>
              {(ageMinValidation && ageMaxValidation) ? '✓' : 'ℹ️'} Age Range: {formValues?.metaAgeMin || '?'} - {formValues?.metaAgeMax || '?'}
            </div>
            {(!ageMinValidation || !ageMaxValidation) && (
              <div style={{ fontSize: '0.9em', color: '#856404', marginTop: '4px' }}>
                Please enter valid ages between 13 and 65
              </div>
            )}
          </div>
        )}
      </div>

      {/* Product Set ID */}
      <div className="form__form-group">
        <span className="form__form-group-label">
          Product Set ID{' '}
          <Tooltip
            id="metaProductSetId"
            msg="Meta product set ID for this budget"
            placement="top"
          />
        </span>
        <div className="form__form-group-field">
          <Field
            name="productSetId"
            component={renderFieldForm}
            placeholder="e.g., 987654321098765"
          />
        </div>
      </div>

      {/* Languages Multi-Select */}
      <div className="form__form-group">
        <span className="form__form-group-label">
          Languages{' '}

          <Tooltip
            id="metaLanguages"
            msg="Select the languages to target for your Meta ads"
            placement="top"
          />
        </span>
        <div className="form__form-group-field">
          <Field
            name="metaLanguages"
            component={renderSelectField}
            options={LANGUAGE_OPTIONS}
            isMulti
          />
        </div>
      </div>

      {/* Geo Targeting - Countries only (no states/cities/postal codes for Meta) */}
      <GeoTargetingFields
        setupFlow={setupFlow}
        agencyInternal={agencyInternal}
        platform="meta"
        supportsStates={false}
        supportsCities={false}
        supportsPostalCodes={false}
      />

      {/* Custom Audiences - dynamic include list */}
      <AudienceFields
        name="customAudienceIds"
        label="Custom Audiences"
        tooltip="Add custom audience IDs to include in targeting"
        placeholder="Custom audience ID (e.g., 120212525218150354)"
        addButtonLabel="Add Audience"
      />

      {/* Excluded Custom Audiences - dynamic exclude list */}
      <AudienceFields
        name="excludedCustomAudienceIds"
        label="Excluded Custom Audiences"
        tooltip="Add custom audience IDs to exclude from targeting"
        placeholder="Custom audience ID to exclude (e.g., 120211497632310354)"
        addButtonLabel="Add Excluded Audience"
      />
    </>
  );
};


MetaBudgetFields.propTypes = {
  budgetTypeOption: PropTypes.shape({
    value: PropTypes.string,
    adType: PropTypes.string,
    itemType: PropTypes.string,
    fields: PropTypes.shape({
      url: PropTypes.bool,
      targeting: PropTypes.bool,
    }),
  }),
  setupFlow: PropTypes.shape({
    value: PropTypes.string,
  }),
  agencyInternal: PropTypes.shape({
    value: PropTypes.bool,
  }),
  formValues: PropTypes.shape({
    masterItemOnList: PropTypes.string,
    metaBrandName: PropTypes.string,
    metaGender: PropTypes.shape({
      value: PropTypes.number,
    }),
    metaAgeMin: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    metaAgeMax: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    metaLanguages: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.string,
    })),
  }),
};

MetaBudgetFields.defaultProps = {
  budgetTypeOption: null,
  setupFlow: null,
  agencyInternal: null,
  formValues: {},
};

export default MetaBudgetFields;
